'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import { LocalFile, isRawFile } from './UploadZone';

interface UploadPreviewGridProps {
  files: LocalFile[];
  onRemove: (id: string) => void;
}

/**
 * Dense, scroll-contained thumbnail grid. Tiles stay small so an order with
 * hundreds of images remains compact, while each still shows its filename.
 */
export default function UploadPreviewGrid({ files, onRemove }: UploadPreviewGridProps) {
  return (
    <div
      className="max-h-[188px] overflow-y-auto pr-1 custom-scrollbar grid gap-2"
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))' }}
    >
      <AnimatePresence mode="popLayout">
        {files.map((f) => (
          <motion.div
            key={f.id}
            layout
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            title={f.file.name}
            className="group relative"
          >
            <div className="relative aspect-square rounded-md overflow-hidden border border-white/[0.08] bg-[#0B0D17]">
              {f.preview && !isRawFile(f.file.name) ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={f.preview} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  <span className="text-[9px] font-bold uppercase leading-none">
                    {f.file.name.split('.').pop()}
                  </span>
                </div>
              )}

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(f.id);
                }}
                className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove"
              >
                <Icon name="XMarkIcon" size={14} variant="outline" className="text-white" />
              </button>
            </div>

            <p className="mt-1 text-[10px] leading-tight text-gray-400 truncate text-center">
              {f.file.name}
            </p>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
